import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import notify from "../../common/ui/notify";
import { LoadingBlock, ErrorBlock } from "../../common/ui/StateBlock";
import { formatPrice, formatUsdFromGel, carTitle, toImageArray, formatMileage, formatEngine, display, isBlank, translateSpec } from "../../common/format";
import { getSocket, emitAck } from "../../common/auctionSocket";
import { currentLanguage } from "../../common/ui/notify";
import useWatchlist from "../../common/useWatchlist";
import WatchButton from "./WatchButton";
import { t } from "./auctionI18n";
import "./Auction.css";
import "../../common/ui/ui.css";

const REASON = {
    too_low: { en: "Your bid is below the minimum.", geo: "ბიდი მინიმუმზე დაბალია.", ru: "Ставка ниже минимума.", am: "Հայտը նվազագույնից ցածր է։" },
    insufficient_deposit: { en: "Not enough free deposit (need 10% of your bid).", geo: "დეპოზიტი არ არის საკმარისი (საჭიროა ბიდის 10%).", ru: "Недостаточно свободного депозита (нужно 10% от ставки).", am: "Բավարար ազատ ավանդ չկա (պետք է հայտի 10%-ը)։" },
    phone_not_verified: { en: "Verify your phone first.", geo: "ჯერ დაადასტურეთ ტელეფონი.", ru: "Сначала подтвердите телефон.", am: "Նախ հաստատեք հեռախոսը։" },
    kyc_not_approved: { en: "Your KYC must be approved first.", geo: "ჯერ უნდა დადასტურდეს KYC.", ru: "Сначала должен быть одобрен KYC.", am: "Նախ պետք է հաստատվի KYC-ը։" },
    not_authenticated: { en: "Please sign in to bid.", geo: "ბიდის დასადებად შედით სისტემაში.", ru: "Войдите, чтобы делать ставки.", am: "Մուտք գործեք հայտ դնելու համար։" },
    lot_not_open: { en: "This lot is not open for bidding.", geo: "ლოტი ბიდისთვის დახურულია.", ru: "Этот лот закрыт для ставок.", am: "Այս լոտը փակ է հայտերի համար։" },
    lot_ended: { en: "This lot has ended.", geo: "ლოტი დასრულდა.", ru: "Этот лот завершён.", am: "Այս լոտն ավարտվել է։" },
    event_paused: { en: "The auction is paused.", geo: "აუქციონი შეჩერებულია.", ru: "Аукцион приостановлен.", am: "Աճուրդը դադարեցված է։" },
    not_higher_than_your_max: { en: "Enter more than your current max.", geo: "შეიყვანეთ თქვენს მაქსიმუმზე მეტი.", ru: "Введите больше вашего текущего максимума.", am: "Մուտքագրեք ձեր ընթացիկ առավելագույնից ավելին։" },
    lot_busy: { en: "Too busy, try again.", geo: "დატვირთულია, სცადეთ თავიდან.", ru: "Слишком занято, попробуйте снова.", am: "Զբաղված է, փորձեք կրկին։" },
    no_buy_now: { en: "Buy-now isn't available for this lot.", geo: "ამ ლოტზე ახლავე ყიდვა ხელმისაწვდომი არ არის.", ru: "Покупка сейчас недоступна для этого лота.", am: "Այս լոտի համար անմիջապես գնումը հասանելի չէ։" },
    buy_now_outbid: { en: "Another bidder's max exceeds the buy-now price.", geo: "სხვა ბიდერის მაქსიმუმი ყიდვის ფასს აღემატება.", ru: "Максимум другого участника превышает цену покупки.", am: "Մեկ այլ մասնակցի առավելագույնը գերազանցում է գնման գինը։" },
};

function fmtRemaining(ms) {
    if (ms == null) return "—";
    if (ms <= 0) return "00:00";
    const s = Math.floor(ms / 1000);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

const AuctionLot = () => {
    const { id } = useParams();
    const lotId = parseInt(id, 10);
    const lang = currentLanguage();

    const [lot, setLot] = useState(null);       // static car/lot info (HTTP)
    const [live, setLive] = useState(null);     // authoritative state (socket)
    const [me, setMe] = useState(null);         // { id, eligible }
    const [loadState, setLoadState] = useState("loading");
    const [maxInput, setMaxInput] = useState("");
    const [now, setNow] = useState(Date.now());
    const [busy, setBusy] = useState(false);
    const [closed, setClosed] = useState(null);
    const [activeImg, setActiveImg] = useState(0);
    const liveRef = useRef(live);
    liveRef.current = live;
    const watch = useWatchlist();

    // Static lot + car info and my identity.
    useEffect(() => {
        let alive = true;
        axios.get(`/api/auction/lots/${lotId}`)
            .then((res) => { if (alive) { setLot(res.data.lot); setLoadState("ready"); } })
            .catch(() => { if (alive) setLoadState("error"); });
        axios.get("/api/auction/me")
            .then((res) => { if (alive) setMe({ id: res.data.user.id, eligibility: res.data.eligibility }); })
            .catch(() => { if (alive) setMe(null); });
        return () => { alive = false; };
    }, [lotId]);

    // Socket: join the lot and stream authoritative state.
    useEffect(() => {
        if (!Number.isInteger(lotId)) return undefined;
        const socket = getSocket();

        const onState = (s) => { if (s && s.lot_id === lotId) setLive(s); };
        const onExtended = (m) => { if (m && m.lot_id === lotId) { setLive((p) => (p ? { ...p, ends_at: m.ends_at } : p)); notify.info({ en: "Time extended!", geo: "დრო გაგრძელდა!", ru: "Время продлено!", am: "Ժամանակը երկարաձգվեց!" }); } };
        const onOutbid = (m) => { if (m && m.lot_id === lotId) notify.warning({ en: "You've been outbid!", geo: "თქვენ გადაგაჭარბეს!", ru: "Вашу ставку перебили!", am: "Ձեր հայտը գերազանցվեց!" }); };
        const onClosed = (m) => { if (m && m.result && m.result.lot_id === lotId) { setClosed(m.result); setLive((p) => (p ? { ...p, status: m.result.status || "closed" } : p)); } };
        const onOutcome = (m) => { if (m && m.lot_id === lotId) {
            if (m.decision === "accepted") notify.success({ en: "You won this lot! An invoice has been created.", geo: "თქვენ მოიგეთ ლოტი! ინვოისი შეიქმნა.", ru: "Вы выиграли этот лот! Счёт создан.", am: "Դուք շահեցիք այս լոտը! Հաշիվը ստեղծվել է։" });
            else if (m.decision === "countered") notify.info({ en: "The seller sent a counter-offer — check your account.", geo: "გამყიდველმა შემოგთავაზათ ფასი — იხილეთ ანგარიში.", ru: "Продавец прислал встречное предложение — проверьте аккаунт.", am: "Վաճառողը հակաառաջարկ ուղարկեց — ստուգեք ձեր հաշիվը։" });
        } };

        socket.on("lot:state", onState);
        socket.on("lot:extended", onExtended);
        socket.on("outbid", onOutbid);
        socket.on("lot:closed", onClosed);
        socket.on("lot:outcome", onOutcome);
        emitAck("lot:join", { lot_id: lotId }).then((ack) => { if (ack && ack.state) setLive(ack.state); });

        return () => {
            socket.off("lot:state", onState);
            socket.off("lot:extended", onExtended);
            socket.off("outbid", onOutbid);
            socket.off("lot:closed", onClosed);
            socket.off("lot:outcome", onOutcome);
        };
    }, [lotId]);

    // Local countdown tick (display only; the server clock is authoritative).
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 500);
        return () => clearInterval(t);
    }, []);

    const place = useCallback(async (event, amount) => {
        if (busy) return;
        if (!me) { notify.warning(REASON.not_authenticated); return; }
        setBusy(true);
        try {
            const ack = await emitAck(event, event === "proxy:set" ? { lot_id: lotId, max: amount } : { lot_id: lotId, amount });
            if (ack && ack.ok) {
                notify.success(ack.leading ? { en: "You're leading!", geo: "თქვენ ლიდერობთ!", ru: "Вы лидируете!", am: "Դուք առաջատար եք!" } : { en: "Bid placed.", geo: "ბიდი დადებულია.", ru: "Ставка сделана.", am: "Հայտը դրված է։" });
                if (ack.state) setLive(ack.state);
            } else {
                const reason = ack && ack.reason;
                notify.error(REASON[reason] || { en: "Bid rejected.", geo: "ბიდი უარყოფილია.", ru: "Ставка отклонена.", am: "Հայտը մերժվեց։" });
            }
        } finally { setBusy(false); }
    }, [busy, me, lotId]);

    const buyNow = useCallback(async (buyNowPrice) => {
        if (busy) return;
        if (!me) { notify.warning(REASON.not_authenticated); return; }
        if (!window.confirm(t(
            `ავტომობილის დაუყოვნებლივ შეძენა ფასად ${formatPrice(buyNowPrice, "GEL")}? დადასტურებისას ლოტი დაიხურება თქვენს სასარგებლოდ და შეიქმნება ინვოისი.`,
            `Buy this car now for ${formatPrice(buyNowPrice, "GEL")}? On confirmation the lot closes in your favour and an invoice is created.`,
            `Купить этот автомобиль сейчас за ${formatPrice(buyNowPrice, "GEL")}? После подтверждения лот закроется в вашу пользу и будет создан счёт.`,
            `Գնե՞լ այս մեքենան հիմա ${formatPrice(buyNowPrice, "GEL")}-ով։ Հաստատելուց հետո լոտը կփակվի ձեր օգտին և կստեղծվի հաշիվ։`
        ))) return;
        setBusy(true);
        try {
            const ack = await emitAck("bid:buynow", { lot_id: lotId });
            if (ack && ack.ok) {
                notify.success({ en: "Purchased! An invoice has been created.", geo: "შეძენილია! ინვოისი შეიქმნა.", ru: "Куплено! Счёт создан.", am: "Գնված է! Հաշիվը ստեղծվել է։" });
            } else {
                notify.error(REASON[ack && ack.reason] || { en: "Buy-now failed.", geo: "ახლავე ყიდვა ვერ შესრულდა.", ru: "Покупка сейчас не удалась.", am: "Անմիջապես գնումը ձախողվեց։" });
            }
        } finally { setBusy(false); }
    }, [busy, me, lotId]);

    if (loadState === "loading") return <div className="container auction-narrow"><LoadingBlock label={t("იტვირთება...", "Loading...", "Загрузка...", "Բեռնվում է...")} /></div>;
    if (loadState === "error" || !lot) return <div className="container auction-narrow"><ErrorBlock title={t("ვერ მოიძებნა", "Not found", "Не найдено", "Չգտնվեց")} message={t("ლოტი ვერ მოიძებნა.", "Lot not found.", "Лот не найден.", "Լոտը չգտնվեց։")} /></div>;

    const price = live ? live.current_price : (lot.current_price || lot.start_price);
    const usdRate = lot && lot.usd_rate ? Number(lot.usd_rate) : null;
    const status = (live && live.status) || lot.status;
    const minNext = live && live.min_next_bid != null ? live.min_next_bid : Number(lot.start_price);
    const isLive = status === "live";
    const endsAt = live && live.ends_at ? new Date(live.ends_at).getTime() : null;
    const remaining = endsAt ? endsAt - now : null;
    const leadingMe = me && live && live.leading === me.id;
    const images = toImageArray(lot.images);
    const canBid = me && me.eligibility && me.eligibility.eligible;
    const buyNowPrice = lot.buy_now_price != null && Number(lot.buy_now_price) > 0 ? Number(lot.buy_now_price) : null;
    const canBuyNow = isLive && buyNowPrice != null && Number(price) < buyNowPrice;

    const specs = [
        { label: t("წელი", "Year", "Год", "Տարի"), value: lot.year },
        { label: "VIN", value: lot.vin },
        { label: t("გარბენი", "Mileage", "Пробег", "Վազք"), value: isBlank(lot.millage) ? null : formatMileage(lot.millage) },
        { label: t("საწვავი", "Fuel", "Топливо", "Վառելիք"), value: translateSpec("fuel", lot.fuel, lang) },
        { label: t("ძრავი", "Engine", "Двигатель", "Շարժիչ"), value: isBlank(lot.engine) ? null : formatEngine(lot.engine) },
        { label: t("ცილინდრები", "Cylinders", "Цилиндры", "Մխոցներ"), value: lot.cilinder },
        { label: t("გადაცემათა კოლოფი", "Gearbox", "Коробка передач", "Փոխանցումատուփ"), value: translateSpec("transmission", lot.transmission, lang) },
        { label: t("წამყვანი თვლები", "Drive", "Привод", "Քարշ"), value: translateSpec("drive", lot.drive, lang) },
        { label: t("კარები", "Doors", "Двери", "Դռներ"), value: lot.doors },
        { label: t("უსაფრთხოების ბალიშები", "Airbags", "Подушки безопасности", "Անվտանգության բարձիկներ"), value: lot.airbags },
        { label: t("ფერი", "Color", "Цвет", "Գույն"), value: translateSpec("color", lot.color, lang) },
        { label: t("კატეგორია", "Category", "Категория", "Կատեգորիա"), value: translateSpec("category", lot.category, lang) },
        { label: t("ლოკაცია", "Location", "Местоположение", "Տեղ"), value: translateSpec("location", lot.location, lang) },
    ].filter((r) => !isBlank(r.value));
    const features = Array.isArray(lot.features) ? lot.features : [];

    return (
        <div className="container auction-lot-room">
            <Link to={`/auction/event/${lot.event_id}`} className="auction-back">← {t("აუქციონზე დაბრუნება", "Back to auction", "Назад к аукциону", "Վերադառնալ աճուրդ")}</Link>

            <div className="auction-room-grid">
                <div className="auction-room-media">
                    <div className="auction-room-image" style={images[activeImg] ? { backgroundImage: `url('${images[activeImg]}')` } : {}} />
                    {images.length > 1 && (
                        <div className="auction-room-thumbs">
                            {images.slice(0, 10).map((img, i) => (
                                <button key={i} type="button" aria-label={`photo ${i + 1}`}
                                    className={`auction-room-thumb ${i === activeImg ? "is-active" : ""}`}
                                    style={{ backgroundImage: `url('${img}')` }} onClick={() => setActiveImg(i)} />
                            ))}
                        </div>
                    )}
                    <div className="auction-room-titlerow">
                        <h1>{carTitle(lot)}</h1>
                        <WatchButton watched={watch.has(lot.car_id)} onToggle={() => watch.toggle(lot.car_id)} />
                    </div>
                    {!isBlank(lot.location) && <p className="auction-muted auction-room-loc">{translateSpec("location", display(lot.location), lang)}</p>}

                    {specs.length > 0 && (
                        <section className="auction-specs-section">
                            <h2 className="auction-section-title">{t("ავტომობილის მონაცემები", "Car details", "Характеристики", "Մեքենայի տվյալներ")}</h2>
                            <dl className="auction-specs">
                                {specs.map((r) => (
                                    <div className="auction-spec-row" key={r.label}>
                                        <dt>{r.label}</dt>
                                        <dd>{display(r.value)}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    )}

                    {!isBlank(lot.description) && (
                        <section className="auction-specs-section">
                            <h2 className="auction-section-title">{t("აღწერა", "Description", "Описание", "Նկարագրություն")}</h2>
                            <p className="auction-desc">{display(lot.description)}</p>
                        </section>
                    )}

                    {features.length > 0 && (
                        <section className="auction-specs-section">
                            <h2 className="auction-section-title">{t("აღჭურვილობა", "Features", "Оснащение", "Հագեցվածություն")}</h2>
                            <ul className="auction-features">
                                {features.map((f, i) => <li key={`${f}-${i}`}>{f}</li>)}
                            </ul>
                        </section>
                    )}
                </div>

                <div className="cx-card auction-room-panel">
                    <div className={`auction-timer ${!isLive ? "is-static" : (remaining != null && remaining <= 10000 ? "is-urgent" : "")}`}>
                        {isLive ? fmtRemaining(remaining) : (status === "open" ? t("წინასწარი ბიდი", "Pre-bid", "Предв. ставка", "Նախնական հայտ") : t("დასრულებული", "Finished", "Завершён", "Ավարտված"))}
                    </div>

                    <div className="auction-bigprice">
                        <span className="auction-muted">{t("მიმდინარე ფასი", "Current price", "Текущая цена", "Ընթացիկ գին")}</span>
                        <strong>{formatPrice(price, "GEL")}</strong>
                        {usdRate && formatUsdFromGel(price, usdRate) && (
                            <span className="auction-usd-block">≈ {formatUsdFromGel(price, usdRate)}</span>
                        )}
                    </div>

                    <div className="auction-badges">
                        {leadingMe
                            ? <span className="auction-lead is-me">{t("თქვენ ლიდერობთ", "You're leading", "Вы лидируете", "Դուք առաջատար եք")}</span>
                            : (live && live.leading ? <span className="auction-lead">{t("ლიდერობს სხვა", "Someone else leads", "Лидирует другой", "Առաջատարն ուրիշ է")}</span> : <span className="auction-lead is-none">{t("ბიდი არ არის", "No bids", "Нет ставок", "Հայտ չկա")}</span>)}
                        {lot.has_reserve && (
                            <span className={`auction-reserve ${live && live.reserve_met ? "is-met" : "is-not"}`}>
                                {live && live.reserve_met
                                    ? t("რეზერვი დაკმაყოფილდა", "reserve met", "резерв достигнут", "ռեզերվը բավարարված է")
                                    : t("რეზერვი არ დაკმაყოფილდა", "reserve not met", "резерв не достигнут", "ռեզերվը չի բավարարվել")}
                            </span>
                        )}
                    </div>

                    {closed ? (
                        <div className="auction-closed">
                            <strong>{t("ლოტი დასრულდა", "Lot finished", "Лот завершён", "Լոտն ավարտվեց")}</strong>
                            <span className="auction-muted">
                                {closed.decision === "accepted" || closed.status === "sold" ? t("გაყიდულია", "Sold", "Продано", "Վաճառված") :
                                 closed.decision === "pending_seller" ? t("ელოდება გამყიდველის გადაწყვეტილებას", "Awaiting the seller's decision", "Ожидает решения продавца", "Սպասում է վաճառողի որոշմանը") :
                                 closed.status === "withdrawn" ? t("მოხსნილია", "Withdrawn", "Снято", "Հանված") : t("დახურულია", "Closed", "Закрыто", "Փակված")}
                            </span>
                        </div>
                    ) : (status === "open" || status === "live") ? (
                        <>
                            {!me && (
                                <p className="auction-muted">{t("ბიდის დასადებად", "To bid,", "Чтобы сделать ставку,", "Հայտ դնելու համար")} <Link to="/auction/login">{t("შედით", "sign in", "войдите", "մուտք գործեք")}</Link> {t("ან", "or", "или", "կամ")} <Link to="/auction/register">{t("დარეგისტრირდით", "register", "зарегистрируйтесь", "գրանցվեք")}</Link>.</p>
                            )}
                            {me && !canBid && (
                                <p className="auction-field-error">{t("ბიდისთვის დაასრულეთ ვერიფიკაცია —", "Complete verification to bid —", "Для ставки завершите верификацию —", "Հայտի համար ավարտեք վերիֆիկացիան —")} <Link to="/auction/account">{t("ანგარიში", "account", "аккаунт", "հաշիվ")}</Link>.</p>
                            )}
                            <button className="cx-btn cx-btn-primary auction-bid-btn" disabled={busy || !canBid} onClick={() => place("bid:place", minNext)}>
                                {t("ბიდი", "Bid", "Ставка", "Հայտ")} {formatPrice(minNext, "GEL")}
                            </button>
                            {canBuyNow && (
                                <button className="cx-btn auction-buynow-btn" disabled={busy || !canBid} onClick={() => buyNow(buyNowPrice)}>
                                    <span className="auction-buynow-label">⚡ {t("ახლავე ყიდვა", "Buy now", "Купить сейчас", "Գնել հիմա")}</span>
                                    <span className="auction-buynow-price">
                                        {formatPrice(buyNowPrice, "GEL")}
                                        {usdRate && formatUsdFromGel(buyNowPrice, usdRate) ? <em> ≈ {formatUsdFromGel(buyNowPrice, usdRate)}</em> : null}
                                    </span>
                                </button>
                            )}
                            <div className="auction-max">
                                <input className="form-control" inputMode="numeric" placeholder={t("მაქს. ფასი (ავტო-ბიდი)", "Max price (auto-bid)", "Макс. цена (авто-ставка)", "Առավ. գին (ավտո-հայտ)")}
                                    value={maxInput} onChange={(e) => setMaxInput(e.target.value)} />
                                <button className="cx-btn cx-btn-ghost" disabled={busy || !canBid || !maxInput}
                                    onClick={() => { place("proxy:set", Number(maxInput)); setMaxInput(""); }}>
                                    {t("მაქს-ის დაყენება", "Set max", "Задать макс.", "Սահմանել առավ.")}
                                </button>
                            </div>
                            <p className="auction-muted auction-min-hint">{t("მინ. შემდეგი ბიდი:", "Min next bid:", "Мин. след. ставка:", "Նվազ. հաջորդ հայտ:")} {formatPrice(minNext, "GEL")}</p>
                        </>
                    ) : (
                        <p className="auction-muted">{t("ეს ლოტი ბიდისთვის დახურულია.", "This lot is closed for bidding.", "Этот лот закрыт для ставок.", "Այս լոտը փակ է հայտերի համար։")}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuctionLot;
